import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { analyticsService, networkActionsErrorsManager, prospectRequestManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { ProspectCampaigns, ProspectRequestConfig } from "@domain/prospect-request/prospect-request";

import styled from "@emotion/styled";
import { TextInput } from "@ui/components/inputs/text-input";
import { FormField } from "@ui/pages/prospect/components/form-field";
import { RoundedButton } from "@ui/pages/prospect/components/rounded-button";
import { PtInfoControls } from "@ui/pages/prospect/pt-info-controls";
import { PtInfoFormData } from "@ui/pages/prospect/pt-info-form-data";
import { physioMeTabletBreakpoint, row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { Checkbox } from "primereact/checkbox";
import React, { useCallback, useState } from "react";
import { Controller, SubmitErrorHandler, useForm } from "react-hook-form";

export interface InviteYourPtFormData extends PtInfoFormData {
	patientFirstName: string;
	patientLastName: string;
	shareDataConsent: boolean;
}

interface InviteYourPtFormProps {
	config: ProspectRequestConfig;
	onSuccess: () => void;
}

export const InviteYourPtForm: React.FC<InviteYourPtFormProps> = ({ config, onSuccess }) => {
	const { format } = useI18n();
	const [privacyNoticeUrl, setPrivacyNoticeUrl] = useState<string>("");

	const { control, handleSubmit, resetField, watch, clearErrors } = useForm<InviteYourPtFormData>({
		defaultValues: {
			firstName: "",
			lastName: "",
			countryCode: config.initialCountryCode,
			postalCode: "",
			phoneNumber: "",
			medicalProfession: undefined,
			shareDataConsent: false,
			patientFirstName: "",
			patientLastName: "",
		},
		shouldFocusError: false,
	});

	const openPatientPrivacyNotice = useCallback(() => {
		window.open(privacyNoticeUrl, "_blank");
	}, [privacyNoticeUrl]);

	const selectedCountryCode = watch("countryCode");

	const [submitForm, isSubmitting] = useAsyncOperation(
		async (data: InviteYourPtFormData) => {
			try {
				await prospectRequestManager.sendInviteYourPTRequest(data);
				analyticsService.trackEvent({
					category: AnalyticsCategory.PROSPECT,
					action: "successSubmit",
					params: [ProspectCampaigns.INVITE_YOUR_PT, data.countryCode, data.medicalProfession ?? "null"],
				});
				onSuccess();
			} catch (e) {
				console.error("Failed to submit demo request", e);
				analyticsService.trackEvent({
					category: AnalyticsCategory.PROSPECT,
					action: "failSubmit",
					params: [ProspectCampaigns.INVITE_YOUR_PT],
				});
				networkActionsErrorsManager.reportError(e);
				throw e;
			}
		},

		{},
	);

	const onFormErrorSubmit: SubmitErrorHandler<InviteYourPtFormData> = fieldErrors => {
		const showAnalytics = Object.values(fieldErrors).every(field => field.type !== "required");
		if (showAnalytics) {
			analyticsService.trackEvent({
				category: AnalyticsCategory.PROSPECT,
				action: "failSubmit",
				params: [ProspectCampaigns.INVITE_YOUR_PT],
			});
		}
	};

	return (
		<FormContainer onSubmit={handleSubmit(submitForm, onFormErrorSubmit)}>
			<div className="p-size-s-bold">{format("inviteYourPt.form.ptSection.title")}</div>
			<PtInfoControls
				initialCountryCode={selectedCountryCode}
				config={config}
				control={control}
				resetField={resetField}
				clearErrors={clearErrors}
				campaign={ProspectCampaigns.INVITE_YOUR_PT}
				onPrivacyNoticeUrlChange={setPrivacyNoticeUrl}
			/>

			<Separator />

			<div className="p-size-s-bold">{format("inviteYourPt.form.patientSection.title")}</div>
			<TwoColumnLayout>
				<Controller
					name="patientFirstName"
					control={control}
					rules={{
						required: format("global.form.field.requiredError"),
					}}
					render={({ field, fieldState }) => (
						<FormField
							title={format("inviteYourPt.form.patientSection.firstName.title")}
							errorMessage={fieldState.error?.message}
						>
							<TextInput
								{...field}
								placeholder={format("inviteYourPt.form.patientSection.firstName.placeholder")}
								error={fieldState.invalid}
								inputMode="text"
								autoCorrect="off"
								autoCapitalize="none"
							/>
						</FormField>
					)}
				/>
				<Controller
					name="patientLastName"
					control={control}
					rules={{
						required: format("global.form.field.requiredError"),
					}}
					render={({ field, fieldState }) => (
						<FormField
							title={format("inviteYourPt.form.patientSection.lastName.title")}
							errorMessage={fieldState.error?.message}
						>
							<TextInput
								{...field}
								placeholder={format("inviteYourPt.form.patientSection.lastName.placeholder")}
								error={fieldState.invalid}
								inputMode="text"
								autoCorrect="off"
								autoCapitalize="none"
							/>
						</FormField>
					)}
				/>
			</TwoColumnLayout>

			<Controller
				name="shareDataConsent"
				control={control}
				rules={{
					required: format("inviteYourPt.form.patientSection.consentCheckbox.requiredError"),
				}}
				render={({ field, fieldState }) => (
					<FormField errorMessage={fieldState.error?.message}>
						<CheckboxContainer>
							<StyledCheckbox inputId="inviteYourPtShareDataConsent" {...field} checked={field.value} />
							<label htmlFor="inviteYourPtShareDataConsent">
								{format("inviteYourPt.form.patientSection.consentCheckbox.label")}
							</label>
						</CheckboxContainer>
					</FormField>
				)}
			/>

			<div className="p-size-xs-semi-bold">
				{format("inviteYourPt.form.privacyFooter", {
					link: {
						onClick: openPatientPrivacyNotice,
						className: "text-primary",
					},
				})}
			</div>

			<StyledButton label={format("inviteYourPt.form.submitButton")} type="submit" loading={isSubmitting} />
		</FormContainer>
	);
};

const FormContainer = styled.form`
	display: grid;
	gap: 16px;
`;

const StyledButton = styled(RoundedButton)`
	margin-top: 24px;
`;

const CheckboxContainer = styled.div`
	${row(16, "flexstart", "flexstart")}
	margin-top: 24px;
`;

const StyledCheckbox = styled(Checkbox)`
	margin-top: 0.125rem;
`;

const TwoColumnLayout = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 24px;

	@media ${physioMeTabletBreakpoint} {
		grid-template-columns: unset;
	}
`;

const Separator = styled.div`
	height: 2px;
	background-color: ${colors.grey10};
	margin: 24px 0;
`;
